import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";

export default function Kutuphane() {
  const [all, setAll] = useState([]);
  const [active, setActive] = useState("Tümü");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/content").then(({ data }) => setAll(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = [...new Set(all.map((i) => i.category).filter(Boolean))];
    return ["Tümü", ...set];
  }, [all]);

  const items = active === "Tümü" ? all : all.filter((i) => i.category === active);

  const featured = items.find((i) => i.featured) || items[0];
  const rest = items.filter((i) => i.id !== featured?.id);

  return (
    <>
      <Seo title="Bilgece Kütüphane" description="Makaleler, Bilgece sorular, kitap notları ve düşünceye değer içerikler. Eğitim, ebeveynlik, öğretmenlik, liderlik ve bilgelik üzerine." />
      <section className="pt-36 pb-14 bg-brand-green text-brand-offwhite grain relative overflow-hidden">
        <div className="container-narrow px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-4">Bilgece Kütüphane</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light">Düşünmeye değer içerikler</h1>
          <p className="text-brand-offwhite/75 mt-5 max-w-2xl leading-relaxed">Bilgiyi bilgeliğe dönüştüren makaleler, sorular, kitap notları ve hikâyeler.</p>
        </div>
      </section>

      <section className="section-pad bg-brand-offwhite">
        <div className="container-narrow">
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((c) => (
              <button key={c} onClick={() => setActive(c)} data-testid={`library-filter-${c}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  active === c ? "bg-brand-green text-brand-offwhite border-brand-green" : "bg-brand-cream text-brand-text/70 border-brand-gold-light/40 hover:border-brand-gold"
                }`}>{c}</button>
            ))}
          </div>

          {loading && <p className="text-brand-text/50">Yükleniyor…</p>}
          {!loading && items.length === 0 && <p className="text-brand-text/60" data-testid="library-empty">Bu kategoride henüz içerik yok.</p>}

          {featured && (
            <Reveal>
              <Link to={`/kutuphane/${featured.slug}`} data-testid="library-featured"
                className="group grid md:grid-cols-2 gap-8 bg-brand-cream border border-brand-gold-light/40 rounded-sm overflow-hidden mb-12">
                <div className="h-64 md:h-auto overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">{featured.category} · {featured.content_type}</span>
                  <h2 className="font-serif text-3xl md:text-4xl text-brand-green mt-3">{featured.title}</h2>
                  <p className="text-brand-text/70 mt-4 leading-relaxed">{featured.excerpt}</p>
                  <div className="flex items-center gap-2 mt-5 text-xs text-brand-text/50"><Clock size={14} /> {featured.reading_time} dk okuma · {featured.author}</div>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((it, i) => (
              <Reveal key={it.id} delay={(i % 3) * 0.08}>
                <Link to={`/kutuphane/${it.slug}`} data-testid={`library-card-${it.slug}`}
                  className="group block h-full bg-brand-cream border border-brand-gold-light/40 rounded-sm overflow-hidden transition-transform duration-300 hover:-translate-y-1.5 hover:border-brand-gold">
                  <div className="h-44 overflow-hidden">
                    <img src={it.cover_image} alt={it.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">{it.category}</span>
                    <h3 className="font-serif text-2xl text-brand-green mt-2 leading-tight">{it.title}</h3>
                    <p className="text-brand-text/65 text-sm mt-2 leading-relaxed line-clamp-3">{it.excerpt}</p>
                    <div className="flex items-center gap-2 mt-4 text-xs text-brand-text/50"><Clock size={13} /> {it.reading_time} dk</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
